/**
 * DateHelper is used for processing date string for sending API request or displaying on website
 * Expected date format stored in DB: ISO
 * Note: ISO is UTC+0
 * @author Liwei Yeh <liwei.yeh@adasasistemas.com>
 */

// Library
import { CalendarDate, DateValue } from '@internationalized/date';
import { formatInTimeZone } from 'date-fns-tz';
import { compareAsc } from 'date-fns/compareAsc';
import { compareDesc } from 'date-fns/compareDesc';
import { format } from 'date-fns/format';
import { enAU } from 'date-fns/locale/en-AU';
import { parseISO } from 'date-fns/parseISO';

import { CONSTANT } from '@/constants';

const BRISBANE_TIMEZONE = 'Australia/Brisbane';

export const formatDate = (date: Date, formatStr: string) => {
	return format(date, formatStr, { locale: enAU });
};

/**
 * getCurrentDateTime generates an ISO date string that is current
 * all users no matter the time zone will generate the same value
 * @param none
 * @returns  current time ISO date string
 */
export const getCurrentDateTime = () => {
	return new Date().toISOString();
};

/**
 * Validates if a date is valid
 * @param {Date | null | undefined} date date to validate
 * @returns {boolean} true if date is valid, false otherwise
 */
export const isValidDate = (date: Date | null | undefined): boolean => {
	return date instanceof Date && !isNaN(date.getTime());
};

/**
 * Validates if a time string is valid
 * @param {string} t time string to validate
 * @returns {boolean} true if time string is valid, false otherwise
 */
export const isValidTime = (t: string) => {
	const h24Format = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
	const h12Format = /^(0[1-9]|1[0-2]):[0-5][0-9](AM|PM|am|pm)$/;
	return t && (h24Format.test(t) || h12Format.test(t));
};

/**
 * check whether iso date string is valid
 * @param {string} date ISO datestring
 * @returns  boolean
 */
export const isValidDateString = (date: string) => {
	const res = parseISO(date);
	if (
		!date ||
		typeof date !== 'string' ||
		date === CONSTANT.INVALID_DATE ||
		res.toString() === 'Invalid Date'
	)
		return false;

	return true;
};

/**
 * Check whether date is between two dates.
 * min and max dates are inclusive and time is ignored
 * @param {Date} date  date to check
 * @param {Date} minDate minimum date of the range
 * @param {Date} maxDate maximum date of the range
 */
export const isDateInRange = (date: Date, minDate: Date, maxDate: Date) => {
	const dateOnly = new Date(date.getTime());
	dateOnly.setHours(0, 0, 0);
	const minDateOnly = new Date(minDate.getTime());
	minDateOnly.setHours(0, 0, 0);
	const maxDateOnly = new Date(maxDate.getTime());
	maxDateOnly.setHours(0, 0, 0);
	return dateOnly >= minDateOnly && dateOnly <= maxDateOnly;
};

/**
 * Check whether datetime is between two datetimes.
 * min datetime is exclusive, max dates is inclusive
 * @param {Date} date  datetime to check
 * @param {Date} minDate minimum date of the range
 * @param {Date} maxDate maximum date of the range
 */
export const isDateTimeInRange = (date: Date, minDate: Date, maxDate: Date) => {
	return date > minDate && date <= maxDate;
};

/**
 * Compare two dates at ISO format to check wheter first date is before or equal second one
 * @param {string} dateAIsoString date A at ISO string format
 * @param {string} dateBIsoString  date B at ISO string format
 */
export const isDateBeforeOrEqual = (dateAIsoString: string, dateBIsoString: string) => {
	const dateA = new Date(dateAIsoString);
	const dateB = new Date(dateBIsoString);
	return dateA <= dateB;
};

/**
 * Converts a Date to a Calendar date by using its year, month and day
 * @param {Date} date the date to convert
 * @returns {string} calendar date in format YYYYMMDD
 */
export const convertDateToCalendarDate = (date: Date) => {
	return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
};

/**
 * Converts a Date to a DateValue type
 * @param {Date} date the date to convert
 * @returns {DateValue} the date as a DateValue object (specifically CalendarDate)
 */
export const convertDateToDateValue = (date: Date): DateValue => {
	return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
};

/**
 * Converts a DateValue or Date object to a standard JavaScript Date
 * @param {Date | DateValue} value - The value to convert, either a Date or DateValue object
 * @returns {Date} A JavaScript Date object
 */
export const convertDateValueToDate = (value: Date | DateValue) => {
	if (value instanceof Date) {
		return value;
	} else {
		return new Date(value.toString());
	}
};

/**
 * Converts the given date to a string with date in format [YYYY<separator>MM<separator>DD]
 * @param {Date} date the date to convert
 * @param {string} separator the character to apply between date fields. Default is '-'.
 * @returns {string} date string in format YYYYMMDD
 * @example '2017-01-14', '2017/01/14'
 */
export const convertDatetoStrYYYYMMDD = (date: Date, separator = '-') => {
	return [
		date.getFullYear(),
		(date.getMonth() + 1).toString().padStart(2, '0'),
		date.getDate().toString().padStart(2, '0'),
	].join(separator);
};

/**
 * Converts the given date to a string with date and time in format [YYYY<separator>MM<separator>DD<separator>HHMM]
 * Hours and minutes are shown together, without separator.
 * @param {Date} date the date to convert
 * @param {string} separator the character to apply between date fields and time. Default is '-'.
 * @returns {string} date string in format YYYYMMDDHHMM
 * @example '2017-01-14-1645', '2017/01/14/1645'
 */
export const convertDateToStrYYYYMMDDHHMM = (date: Date, separator = '-') => {
	const formattedDate = convertDatetoStrYYYYMMDD(date, separator);
	const hoursAndMinutes =
		date.getHours().toString().padStart(2, '0') + date.getMinutes().toString().padStart(2, '0');
	return [formattedDate, hoursAndMinutes].join(separator);
};

/**
 * convertDateAndTimeToDisplayFormat takes in ISO string and generates user friendly date format
 * @param {string} isoString: ISO date string or raw date string (YYYY-MM-DD)
 * @param {'date' | 'datetime' | 'time'} type: the time format that date will be displayed in
 * @returns {string} formatted date
 */
export const convertDateAndTimeToDisplayFormat = (
	isoString: string,
	type: 'date' | 'datetime' | 'datetimeZone' | 'datehhmm' | 'time' = 'date',
) => {
	if (!isValidDateString(isoString)) return '';

	const formatObj = {
		date: 'dd/MM/yyyy',
		datetime: 'dd/MM/yyyy E, hh:mm a',
		datetimeZone: 'E dd/MM/yyyy zzz hh:mm a',
		datehhmm: 'dd/MM/yyyy HH:mm',
		time: 'E, hh:mm a',
	};

	try {
		// Check if it's a raw date string (YYYY-MM-DD format without time)
		if (isRawDateFormat(isoString)) {
			// For raw dates, only allow 'date' type formatting to avoid confusion
			if (type !== 'date') {
				console.warn(`Raw date "${isoString}" is defaulting to 'date'.`);
			}
			return format(isoString, formatObj['date']);
		}
		// Handle ISO datetime strings (with T and/or timezone info)
		if (hasTimeInfo(isoString)) {
			return formatInTimeZone(isoString, BRISBANE_TIMEZONE, formatObj[type], { locale: enAU });
		}
		return format(isoString, formatObj['date']);
	} catch {
		console.warn(`Error: "${isoString}" can only be formatted as 'date' type.`);
		return '';
	}
};

/**
 * Helper function to detect if a string is a raw date format (YYYY-MM-DD)
 * @param {string} dateString - The date string to check
 * @returns {boolean} True if it's a raw date format
 */
export const isRawDateFormat = (dateString: string): boolean => {
	return /^\d{4}-\d{2}-\d{2}$/.test(dateString?.trim());
};

/**
 * Helper function to detect if a string contains time/timezone information
 * @param {string} dateString - The date string to check
 * @returns {boolean} True if it contains time information
 */
export const hasTimeInfo = (dateString: string): boolean => {
	return dateString.includes('T') || dateString.includes('Z') || dateString.includes('+');
};

/**
 * sortDate sort the date of the given array
 * @param {Array<unknown>} dateArray: the array with date string that wish to be sorted
 * @param {Array<unknown>} dateFieldName: if the array is object, this is field name of the object which is the date
 * @param {Array<unknown>} dateOrder: sort order
 * @returns  sorted array based on given order
 */
export const sortDate = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	dateArray: Array<any>,
	dateFieldName: string,
	dateOrder: 'desc' | 'asc',
) => {
	if (!dateArray || !Array.isArray(dateArray)) return [];

	const sorted = dateArray.sort((a, b) => {
		const date1 = new Date(dateFieldName ? a[dateFieldName] : a);
		const date2 = new Date(dateFieldName ? b[dateFieldName] : b);
		if (dateOrder === 'desc') {
			return compareDesc(new Date(date1), new Date(date2));
		} else {
			return compareAsc(new Date(date1), new Date(date2));
		}
	});

	return sorted;
};
